/**
 * ReportErrorBoundary — R110.44
 *
 * Wraps the FeasibilityReport tree so a render-time exception (e.g. a
 * malformed payload, missing field, or a third-party integration blowing up
 * inside a chart) NEVER takes the whole app to a blank white screen.
 *
 * Prod incident recap: users reported the screen going blank right after a
 * report finished generating. Since the report page has no boundary, any
 * runtime error inside its (large) render tree bubbles up to root and React
 * unmounts everything. This boundary contains the failure to *just* this
 * page and gives the user a "your report is saved — try again" fallback.
 */
import { Component } from "react";
import { Button } from "@/components/ui/button";
import { AlertTriangle, RefreshCw, Home as HomeIcon } from "lucide-react";
import { Navbar } from "@/components/venture/Navbar";

export default class ReportErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Console breadcrumb so we can spot recurrences in prod screenshots.
    // eslint-disable-next-line no-console
    console.error("[report-boundary] render error", { message: error?.message, stack: error?.stack, info });
    // Optional GA4 signal for tracking prod recurrence.
    try {
      window.vkTrack && window.vkTrack("report_render_error", {
        message: (error && error.message) ? String(error.message).slice(0, 200) : "unknown",
      });
    } catch (_) { /* noop */ }
  }

  reset = () => {
    this.setState({ error: null });
  };

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div>
        <Navbar />
        <div
          data-testid="report-render-error"
          className="mx-auto max-w-2xl px-5 sm:px-8 py-16 text-center"
        >
          <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto" />
          <h1 className="font-heading mt-4 text-2xl font-bold text-slate-900">
            We couldn't display your report right now.
          </h1>
          <p className="mt-2 text-slate-600">
            Your report is safe — it's saved to your account. Please retry, or open it from your dashboard.
          </p>
          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <Button
              data-testid="report-render-error-retry"
              className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={() => {
                this.reset();
                // Force a full remount by reloading — cheapest way to recover
                // from a corrupt render state without needing to plumb parent
                // callbacks down here.
                setTimeout(() => window.location.reload(), 50);
              }}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
            <Button
              data-testid="report-render-error-dashboard"
              variant="outline"
              className="rounded-full"
              onClick={() => { window.location.href = "/dashboard"; }}
            >
              <HomeIcon className="h-4 w-4 mr-2" />
              Back to dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
